import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Clock,
  Check,
  MessageCircle,
  CalendarPlus,
  ChevronRight,
  Info,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ALL_BOOKABLE,
  SERVICES,
  DEALS,
  BOOKING_FEE,
  openWhatsApp,
} from "@/data/menzy";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
  "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM",
];

const todayStr = () => new Date().toISOString().split("T")[0];

// Build a Google Calendar link from date (YYYY-MM-DD) + slot ("1:00 PM")
function googleCalendarUrl({ serviceName, date, time, total }) {
  const [t, mer] = time.split(" ");
  let [h, m] = t.split(":").map(Number);
  if (mer === "PM" && h !== 12) h += 12;
  if (mer === "AM" && h === 12) h = 0;
  const pad = (n) => String(n).padStart(2, "0");
  const dateNoDash = date.replace(/-/g, "");
  const start = `${dateNoDash}T${pad(h)}${pad(m)}00`;
  const end = `${dateNoDash}T${pad((h + 1) % 24)}${pad(m)}00`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `MENZY — ${serviceName}`,
    dates: `${start}/${end}`,
    details: `Appointment with MENZY (Master Barber). Total: $${total}.`,
    location: "MENZY — Master Barber",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function Bookings() {
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const selected = useMemo(
    () => ALL_BOOKABLE.find((s) => s.id === selectedId),
    [selectedId]
  );
  const total = selected ? selected.price + BOOKING_FEE : 0;

  // Preselect from ?service= and scroll to panel
  useEffect(() => {
    const svc = searchParams.get("service");
    if (svc && ALL_BOOKABLE.some((s) => s.id === svc)) {
      setSelectedId(svc);
      setTimeout(() => {
        document.getElementById("booking-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [searchParams]);

  const selectService = (id) => {
    setSelectedId(id);
    document.getElementById("booking-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const validate = () => {
    if (!selected) return toast.error("Please select a service first."), false;
    if (!date) return toast.error("Please choose a date."), false;
    if (!time) return toast.error("Please choose a time."), false;
    if (!name.trim()) return toast.error("Please enter your full name."), false;
    if (!phone.trim()) return toast.error("Please enter your mobile number."), false;
    return true;
  };

  const openReview = () => {
    if (validate()) setConfirmOpen(true);
  };

  const buildMessage = () => {
    const lines = [
      "Hi MENZY! I'd like to book an appointment.",
      "",
      `*Service:* ${selected.name}`,
      `*Service Price:* $${selected.price}`,
      `*Appointment Fee:* $${BOOKING_FEE}`,
      `*Total:* $${total}`,
      `*Date:* ${date}`,
      `*Time:* ${time}`,
      "",
      `*Name:* ${name}`,
      `*Phone:* ${phone}`,
    ];
    if (email.trim()) lines.push(`*Email:* ${email}`);
    if (notes.trim()) lines.push(`*Notes:* ${notes}`);
    return lines.join("\n");
  };

  const confirmBooking = () => {
    openWhatsApp(buildMessage());
    setConfirmOpen(false);
    setSuccessOpen(true);
  };

  const fieldCls =
    "bg-[#121215] border-[#2a2a31] focus-visible:ring-[#d4af37] text-white placeholder:text-white/35";

  return (
    <div className="bg-[#09090b] text-white">
      {/* ===== BOOKINGS HERO ===== */}
      <section className="relative overflow-hidden pt-[92px] sm:pt-[120px] pb-10 sm:pb-14 border-b border-[#d4af37]/15">
        <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle,#d4af37,transparent 70%)" }} />
        <div className="mx-auto max-w-7xl px-5 sm:px-6 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs sm:text-sm tracking-[0.35em] text-white/70 font-semibold">LOOK GOOD. FEEL BETTER. BE YOU.</p>
            <img src="/assets/logo_full.png" alt="MENZY — Master Barber" className="mt-4 w-[90%] max-w-[440px] sm:max-w-[500px] h-auto" />
            <p className="mt-4 text-sm sm:text-base tracking-[0.15em] text-white/85 font-semibold uppercase">Clean Cuts. Confident You.</p>
          </div>
        </div>
      </section>

      {/* ===== BOOK YOUR SERVICE ===== */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl gold-surface flex items-center justify-center text-[#0b0b0c]">
              <CalendarIcon className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <h2 className="font-display font-black text-3xl sm:text-4xl">Book Your Service</h2>
              <p className="text-white/60 text-sm mt-1">Select your service, choose a date and time, and secure your appointment with Menzy.</p>
            </div>
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            {/* Left: selectable services */}
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {SERVICES.map(({ id, name: sName, price, desc, icon: Icon }) => {
                  const active = selectedId === id;
                  return (
                    <button
                      key={id}
                      onClick={() => selectService(id)}
                      data-testid={`booking-service-card-${id}`}
                      aria-pressed={active}
                      className={`text-left rounded-2xl p-4 flex items-center gap-3 transition-colors duration-300 ${
                        active
                          ? "bg-[#d4af37]/10 border-2 border-[#d4af37] shadow-[0_0_22px_rgba(212,175,55,0.25)]"
                          : "bg-[#121215] border-2 border-[#26262d] hover:border-[#d4af37]/50"
                      }`}
                    >
                      <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${active ? "gold-surface text-[#0b0b0c]" : "bg-[#d4af37]/10 text-[#f4d160]"}`}>
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{sName}</p>
                        <p className="text-xs text-white/50 truncate">{desc}</p>
                      </div>
                      <div className="text-right">
                        <div className="font-display font-black text-lg gold-text">${price}</div>
                        <div className={`text-[10px] uppercase font-bold tracking-wide ${active ? "text-[#f4d160]" : "text-white/40"}`}>
                          {active ? "Selected" : "Select"}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Deals */}
              <div className="mt-5 rounded-2xl border-2 border-[#d4af37]/50 p-4" style={{ background: "linear-gradient(160deg,#1c1810,#101013)" }}>
                <h3 className="font-display font-bold tracking-widest gold-text text-lg mb-3">BARBER SHOP DEALS</h3>
                <div className="grid grid-cols-1 gap-3">
                  {DEALS.map(({ id, name: dName, price, desc, icon: Icon }) => {
                    const active = selectedId === id;
                    return (
                      <button
                        key={id}
                        onClick={() => selectService(id)}
                        data-testid={`booking-service-card-${id}`}
                        aria-pressed={active}
                        className={`text-left rounded-xl p-3.5 flex items-center gap-3 transition-colors duration-300 ${
                          active ? "bg-[#d4af37]/10 border-2 border-[#d4af37]" : "bg-[#0c0c0f]/80 border border-[#d4af37]/30 hover:border-[#d4af37]/70"
                        }`}
                      >
                        <div className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${active ? "gold-surface text-[#0b0b0c]" : "bg-[#d4af37]/15 text-[#f4d160]"}`}>
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm">{dName}</p>
                          <p className="text-xs text-white/50">{desc}</p>
                        </div>
                        <div className="text-right">
                          <div className="font-display font-black text-lg gold-text">${price}</div>
                          <div className={`text-[10px] uppercase font-bold tracking-wide ${active ? "text-[#f4d160]" : "text-white/40"}`}>{active ? "Selected" : "Select"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right: schedule panel */}
            <div id="booking-panel" className="rounded-2xl border-2 border-[#d4af37]/40 bg-[#0d0d10] p-5 sm:p-6 lg:sticky lg:top-24">
              <div className="flex items-center gap-3 pb-4 border-b border-white/10">
                <CalendarIcon className="h-6 w-6 text-[#f4d160]" />
                <div>
                  <h3 className="font-bold uppercase tracking-wide text-sm">Schedule Your Appointment</h3>
                  <p className="text-xs text-white/50">Choose your service, date & time.</p>
                </div>
              </div>

              {/* 1. Service */}
              <div className="mt-5">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">1. Select Service</Label>
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger data-testid="booking-service-select" className={`mt-2 ${fieldCls}`}>
                    <SelectValue placeholder="Choose a service…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121215] border-[#2a2a31] text-white max-h-72">
                    {ALL_BOOKABLE.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="focus:bg-[#d4af37]/20">
                        {s.name} — ${s.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 2. Date */}
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">2. Choose Date</Label>
                <div className="relative mt-2">
                  <Input
                    type="date"
                    min={todayStr()}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="booking-date-input"
                    className={`${fieldCls} [color-scheme:dark]`}
                  />
                </div>
              </div>

              {/* 3. Time */}
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">3. Choose Time</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => setTime(slot)}
                      data-testid={`booking-time-${slot.replace(/[:\s]/g, "")}`}
                      className={`py-2.5 rounded-lg text-xs font-semibold transition-colors duration-200 ${
                        time === slot ? "gold-surface text-[#0b0b0c]" : "bg-[#121215] border border-[#2a2a31] text-white/80 hover:border-[#d4af37]/60"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Info */}
              <div className="mt-5 space-y-3">
                <Label className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold">4. Your Information</Label>
                <Input placeholder="Full Name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name-input" className={fieldCls} />
                <Input placeholder="Mobile Number *" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone-input" className={fieldCls} />
                <Input placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email-input" className={fieldCls} />
                <Textarea placeholder="Special request / notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes-input" className={fieldCls} rows={2} />
              </div>

              {/* Price breakdown */}
              {selected && (
                <div className="mt-5 rounded-xl bg-[#121215] border border-[#2a2a31] p-4 text-sm" data-testid="booking-price-breakdown">
                  <div className="flex justify-between text-white/75">
                    <span>{selected.name}</span>
                    <span>${selected.price}</span>
                  </div>
                  <div className="flex justify-between text-white/75 mt-1.5">
                    <span>Appointment Booking Fee</span>
                    <span>${BOOKING_FEE}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10 flex justify-between items-center">
                    <span className="font-bold uppercase tracking-wide">Total</span>
                    <span className="font-display font-black text-2xl gold-text">${total}</span>
                  </div>
                </div>
              )}

              <button
                onClick={openReview}
                data-testid="booking-review-button"
                className="mt-5 w-full gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-sm py-4 rounded-full flex items-center justify-center gap-2 shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-transform duration-300 hover:scale-[1.02]"
              >
                <CalendarIcon className="h-5 w-5" strokeWidth={2.5} /> Review & Book <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
              <p className="mt-2 text-center text-xs text-white/45">Secure your spot. Look good. Feel better.</p>
            </div>
          </div>

          {/* PLEASE NOTE banner */}
          <div className="mt-8 rounded-2xl gold-surface text-[#0b0b0c] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="booking-note-banner">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 shrink-0" strokeWidth={2} />
              <div>
                <p className="font-display font-black text-lg tracking-wide">PLEASE NOTE:</p>
                <p className="font-bold text-lg">Appointments are $5 additional.</p>
              </div>
            </div>
            <div className="sm:border-l sm:border-[#0b0b0c]/30 sm:pl-5 text-sm font-medium leading-relaxed">
              Walk-ins are welcome, but booking your appointment secures your time and helps ensure a better experience.
              The $5 fee is added on top of the listed service price. Thank you for your support!
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONFIRM DIALOG ===== */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0d0d10] border-2 border-[#d4af37]/50 text-white max-w-md" data-testid="booking-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl gold-text tracking-wide">Confirm Your Appointment</DialogTitle>
            <DialogDescription className="text-white/55">Review your appointment details before booking with Menzy.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 mt-1">
              <div className="rounded-xl bg-[#121215] border border-[#2a2a31] p-4 space-y-2 text-sm">
                <Row label="Service" value={selected.name} />
                <Row label="Service Price" value={`$${selected.price}`} />
                <Row label="Appointment Fee" value={`$${BOOKING_FEE}`} />
                <div className="pt-2 mt-2 border-t border-white/10 flex justify-between items-center">
                  <span className="font-bold uppercase tracking-wide">Total</span>
                  <span className="font-display font-black text-xl gold-text">${total}</span>
                </div>
                <div className="pt-2 border-t border-white/10 space-y-2">
                  <Row label="Date" value={date} />
                  <Row label="Time" value={time} />
                </div>
              </div>
              <div className="rounded-xl bg-[#121215] border border-[#2a2a31] p-4 text-sm">
                <p className="text-xs uppercase tracking-widest text-[#d4af37] font-semibold mb-2">Customer</p>
                <p className="font-semibold">{name}</p>
                <p className="text-white/60">{phone}</p>
                {email && <p className="text-white/60">{email}</p>}
              </div>
              <button
                onClick={confirmBooking}
                data-testid="booking-confirm-button"
                className="w-full gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-sm py-4 rounded-full flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={2.5} /> Confirm Booking
              </button>
              <p className="flex items-center justify-center gap-1.5 text-xs text-white/45">
                <Info className="h-3.5 w-3.5" /> Details are sent to Menzy via WhatsApp to finalize.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== SUCCESS DIALOG ===== */}
      <Dialog open={successOpen} onOpenChange={setSuccessOpen}>
        <DialogContent className="bg-[#0d0d10] border-2 border-[#d4af37]/50 text-white max-w-md" data-testid="booking-success-dialog">
          <DialogHeader>
            <div className="mx-auto h-16 w-16 rounded-full gold-surface flex items-center justify-center text-[#0b0b0c] mb-2">
              <Check className="h-9 w-9" strokeWidth={3} />
            </div>
            <DialogTitle className="text-center font-display font-black text-3xl gold-text tracking-wide">You're Booked.</DialogTitle>
            <DialogDescription className="text-center text-white/55">Your appointment request has been sent to Menzy.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-center text-white/70 text-sm">
                Your appointment with MENZY has been requested. Menzy will confirm shortly via WhatsApp.
              </p>
              <div className="rounded-xl bg-[#121215] border border-[#2a2a31] p-4 space-y-2 text-sm">
                <Row label="Service" value={selected.name} />
                <Row label="Date" value={date} />
                <Row label="Time" value={time} />
                <div className="pt-2 mt-1 border-t border-white/10 flex justify-between items-center">
                  <span className="font-bold uppercase tracking-wide">Total</span>
                  <span className="font-display font-black text-xl gold-text">${total}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href={googleCalendarUrl({ serviceName: selected.name, date, time, total })}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-add-calendar-button"
                  className="border-2 border-[#d4af37] text-[#f4d160] hover:bg-[#d4af37]/10 font-bold uppercase tracking-wide text-xs py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors duration-300"
                >
                  <CalendarPlus className="h-4 w-4" strokeWidth={2.5} /> Add to Calendar
                </a>
                <button
                  onClick={() => openWhatsApp("Hi MENZY! I just booked an appointment.")}
                  data-testid="booking-contact-menzy-button"
                  className="gold-surface text-[#0b0b0c] font-extrabold uppercase tracking-wide text-xs py-3.5 rounded-full flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={2.5} /> Contact Menzy
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-white/55">{label}</span>
    <span className="font-semibold text-right">{value}</span>
  </div>
);
